import { Checkbox, HStack, IconButton, Text, Tooltip } from "@chakra-ui/react";
import { Spinner } from "@chakra-ui/spinner";
import { Contractor } from "@components/modules/contractors/ContractorModel";
import ModalContractor from "@components/modules/contractors/ModalContractor";
import { FormRow } from "@components/ui-kit";
import { Trans, t } from "@lingui/macro";
import { useUpdateContractorMutation } from "@src/__generated__/graphql";
import { Icon } from "@src/components/ui-kit/Icon";
import { fieldToCheckboxProps } from "@src/utils/forms/inputHelpers";
import { FieldState } from "formstate";
import { observer } from "mobx-react-lite";
import React, { type FunctionComponent, useState } from "react";
import { useStore } from "../../store";

interface ContractorDetailsProps {
  loading?: boolean;
}

export const ContractorDetails: FunctionComponent<ContractorDetailsProps> =
  observer(function ContractorDetails({ loading }) {
    const store = useStore();
    const { editContractorStore } = store;

    const [onlyForThisExpense] = useState(() => new FieldState(true));

    const [update, { loading: loadingUpdateContractor }] =
      useUpdateContractorMutation({
        onCompleted({ updateContractor }) {
          store.setContractor(new Contractor(updateContractor));
          editContractorStore.toggleOpen(false);
        },
      });

    const onOpenModal = () => {
      if (!store.contractor) return;
      editContractorStore.toggleOpen(true);
      if (!editContractorStore.form.extraValidationEnabled) return;
      onlyForThisExpense.onChange(false);
    };

    const onCloseModal = () => {
      store.syncContractorWithModal();
    };

    const onEditContractor = async () => {
      const { hasError } = await editContractorStore.form.validateForExpense(
        store.contractorOptionalValidateFields,
      );

      if (hasError) return;

      if (onlyForThisExpense.value) {
        store.setContractor(
          editContractorStore.form.toModel(store.contractor!.id),
        );
        editContractorStore.toggleOpen(false);
      } else {
        update({
          variables: {
            input: {
              id: store.contractor!.id,
              ...editContractorStore.form.serialize(),
            },
          },
        });
      }
    };

    if (loading) {
      return (
        <div className="flex justify-center py-6">
          <Spinner size="lg" />
        </div>
      );
    }

    if (!store.contractor) {
      return null;
    }

    return (
      <React.Fragment>
        <div className="py-6">
          <div className="flex justify-between">
            <div>{store.contractor.street}</div>
            <HStack>
              <Tooltip
                hasArrow
                label={t`Reset contractor data`}
                placement="top"
              >
                <IconButton
                  aria-label={t`Reset contractor data`}
                  icon={<Icon name="refresh-cw-02" />}
                  isLoading={store.isFetchingContractor}
                  onClick={() => store.fetchContractor(store.contractor!.id)}
                  variant="ghost"
                />
              </Tooltip>
              <Tooltip hasArrow label={t`Edit contractor`} placement="top">
                <IconButton
                  aria-label={t`Edit contractor`}
                  icon={<Icon name="pencil-01" />}
                  onClick={onOpenModal}
                  variant="ghost"
                />
              </Tooltip>
            </HStack>
          </div>
          <div>
            {store.contractor.zip} {store.contractor.city}
          </div>
          <div className="pb-6">{store.contractor?.country?.name}</div>
          <div>
            <Trans>Reg. no</Trans>: {store.contractor.registration_no}
          </div>
          <div className="font-bold">
            <Trans>IBAN</Trans>: {store.contractor.iban}
          </div>
          {store.appStore.workspaceStore.settings
            ?.enableContractorFullBankAccountData && (
            <>
              <Text>
                <strong>
                  <Trans>Bank account:</Trans>
                  {store.contractor.bank_account}
                </strong>
              </Text>
              <Text>
                <strong>
                  <Trans>Bank code:</Trans>
                  {store.contractor.bank_account_code}
                </strong>
              </Text>
            </>
          )}
        </div>
        <ModalContractor.StoreContext.Provider value={editContractorStore}>
          <ModalContractor.Modal
            onClose={onCloseModal}
            title={t`Edit contractor`}
            primaryAction={{
              onAction: onEditContractor,
              loading: loadingUpdateContractor,
            }}
          >
            <FormRow title={t`Save changes only for this expense`}>
              <Checkbox
                {...fieldToCheckboxProps(onlyForThisExpense)}
                isDisabled={editContractorStore.form.extraValidationEnabled}
              />
            </FormRow>
          </ModalContractor.Modal>
        </ModalContractor.StoreContext.Provider>
      </React.Fragment>
    );
  });
